import * as R from "ramda";

import { isIntegrated } from "reducers/requirements";
import { Requirement } from "types/entities";
import { sorterOn } from "utils/func";
import { recordsToOptions } from "utils/toOptions";

import { useDepartmentsList } from "./entities";
import resourceOperations, { ReadOptions } from "./resourceOperations";

const sortByName = sorterOn("name");

const operation = resourceOperations({
  basePath: "/api/requirements",
  baseKey: ["requirements"],
});

export const useRequirement = (id: number, opts: ReadOptions = {}) =>
  operation.useRead<Requirement>(id, opts);

export const useRequirements = (opts: ReadOptions = {}) =>
  operation.useRead<Requirement[]>(null, opts);

export const useRequirementsByID = (ids: number[] = [], opts: ReadOptions = {}) =>
  useRequirements({
    ...opts,
    select: R.filter((r: any) => ids.includes(r.id)),
  });

export const useRequirementBySlug = (slug: string, opts: ReadOptions = {}) =>
  useRequirements({
    ...opts,
    select: R.find((r: any) => r.slug === slug),
  });

export const useRequirementByKey = (key: string, opts: ReadOptions = {}) =>
  useRequirements({
    ...opts,
    select: R.find((r: any) => r.key === key),
  });

export const useHasIntegratedRequirements = (opts: ReadOptions = {}) => {
  const { data = [] } = useRequirements({
    ...opts,
    select: R.filter(isIntegrated),
  });
  return data.length > 0;
};

export const useRequirementsOptionsByDepartment = (opts: ReadOptions = {}) => {
  const { data: departmentsById = {}, isLoading } = useDepartmentsList({
    select: R.indexBy(R.prop("id") as (x: any) => number),
  });

  return useRequirements({
    ...opts,
    enabled: !isLoading,
    select: (requirements) => {
      if (opts.select) {
        requirements = opts.select(requirements);
      }
      const requirementsByDepartment = R.groupBy(R.prop("department_id"), requirements);
      const departments = R.props(R.keys(requirementsByDepartment), departmentsById);
      const records = recordsToOptions(sortByName(departments));
      return records.map((record) => ({
        options: recordsToOptions(sortByName(requirementsByDepartment[record.value])),
        ...record,
      }));
    },
  });
};
